import React, { ReactElement } from 'react';
import { Flex, FlexProps } from '@wegroup/design-system';
import useIsAboveViewportHeight from '../../../../../../../common/src/hooks/useIsAboveViewportHeight';
import QuestionGroupAssistantQuestionContent from './QuestionGroupAssistantQuestionContent';
import useMobileBreakpoints from '../../../../../../../common/src/hooks/useMobileBreakpoints';

interface Props {
  question: string;
  description?: string;
  horizontal?: boolean;
  noPadding?: boolean;
  Tag?: ReactElement;
  index?: number;
}

const QuestionGroupAssistantQuestion: React.FC<
  React.PropsWithChildren<Props & FlexProps>
> = ({
  question,
  description,
  horizontal,
  noPadding,
  Tag,
  index,
  ...otherProps
}) => {
  const { isTablet } = useMobileBreakpoints();

  if (horizontal && isTablet)
    return (
      <HorizontalGroupAssistantQuestion
        question={question}
        description={description}
        index={index}
        {...otherProps}
      />
    );

  return (
    <GroupAssistantQuestion
      question={question}
      description={description}
      noPadding={noPadding}
      index={index}
      {...otherProps}
    />
  );
};

type GroupAssistantQuestionProps = QuestionProps & {
  noPadding?: boolean;
};

const GroupAssistantQuestion: React.FC<
  React.PropsWithChildren<GroupAssistantQuestionProps>
> = ({ question, description, noPadding, index, ...otherProps }) => {
  const isSmallH = useIsAboveViewportHeight('hd');

  return (
    <Flex
      flexDir="column"
      alignItems={{ base: 'flext-start', tablet: 'center' }}
      gridGap="0"
      maxW={{ tablet: '2xl' }}
      mx={{ base: 'unset', tablet: 'auto' }}
      data-test-id="Flows_QuestionGroupAssistantQuestion"
      {...(!noPadding && {
        px: { base: '3', laptop: '8' },
        pt: { base: '3', laptop: isSmallH ? '14' : '4' },
      })}
      {...otherProps}
    >
      <QuestionGroupAssistantQuestionContent
        question={question}
        description={description}
        index={index}
      />
    </Flex>
  );
};

interface QuestionProps {
  question: string;
  description?: string;
  index?: number;
}

const HorizontalGroupAssistantQuestion: React.FC<
  React.PropsWithChildren<QuestionProps>
> = ({ question, description, index, ...otherProps }) => {
  return (
    <Flex
      flexDir="row"
      alignItems="flex-start"
      gridGap="6"
      maxW="auto"
      mx="auto"
      px={{ base: '6', laptop: '10' }}
      pt={{ base: '4', laptop: '10' }}
      data-test-id="Flows_QuestionGroupAssistantQuestion"
      {...otherProps}
    >
      <QuestionGroupAssistantQuestionContent
        question={question}
        description={description}
        horizontal
        index={index}
      />
    </Flex>
  );
};

export default QuestionGroupAssistantQuestion;
